import { useContext, useMemo, useState } from 'react'
import { WppButton, WppTypography } from '@wppopen/components-library-react'
import ProductDetail from 'components/ProductDetail/ProductDetail'
import ViewTaskPageContext from 'context/viewTaskPageContext/viewTaskPageContext'
import { GenericModal } from 'components/Modals'
import RenderNavButtons from 'components/RenderNavButtons/RenderNavButtons'
import clsx from 'clsx'
import { returnEmptyPDPMessage } from '../utils'
import viewTaskStyles from '../ViewTaskPage.module.scss'
import AsyncTypography from 'components/AsyncTypography/AsyncTypography'
import usePermissions from 'hooks/usePermissions'
import useCurrentUser from 'hooks/useCurrentUser'
import { useParams } from 'react-router-dom'
import useApi from 'hooks/useApi'
import { setKeyphrases, setKeywords } from 'reducers/KeywordsAndPhrasesReducer/KeywordsAndPhrasesReducer'
import type { ContentTypePatchRequest, ContentTypePatchResponseSuccess, v5 } from '@contracts'
import type { Actions } from '../PdpHeader/PdpHeader-types.d'
import type { OnSubmitGenericModalProps } from 'components/Modals/GenericModal/helper'
import type { FEApiError } from 'utils/errorHandling'
import type { ToastDetails } from 'components/Banner/BannerTypesUtils'

interface PdpViewProps {
  singleJobData: v5.JobsDetailData | null
  isSingleJobLoading?: boolean
  actions?: Actions
  isInitialising?: boolean
  setToastDetails?: React.Dispatch<React.SetStateAction<ToastDetails | undefined>>
}

function PdpView({ singleJobData, isSingleJobLoading, actions, isInitialising, setToastDetails }: PdpViewProps) {
  const { userName } = useCurrentUser()
  const [isRefreshLoading, setIsRefreshLoading] = useState(false)
  const {
    isModalOpen,
    contentTypeBeingEdited,
    setIsModalOpen,
    contentTypeContent,
    setContentTypeBeingEdited,
    singleJobApiMethods,
    keywordsAndPhrasesReducerDispatch,
  } = useContext(ViewTaskPageContext)
  const { job_id } = useParams()
  const { fetchData: getSingleJobData } = singleJobApiMethods
  const isGeneratedContentAvailable =
    singleJobData?.status && !['processing', 'failed', 'blocked', 'executing'].includes(singleJobData.status)
  const { clientLevelPermissions } = usePermissions({
    clientId: singleJobData?.client_id,
    permissionSubset: ['PDP_APP_PATCH_JOBS_BY_CONTENT_TYPES_BY'],
  })
  const patchContentTypeEndpoint = useMemo(
    () => `jobs/${job_id}/content_types/${contentTypeBeingEdited}`,
    [contentTypeBeingEdited, job_id],
  )

  const { fetchData: patchContentType, isLoading: isPatchContentTypeLoading } =
    useApi<ContentTypePatchResponseSuccess>(patchContentTypeEndpoint)

  const [canPatchJob] = clientLevelPermissions
  const outputIDs = (
    <>
      <AsyncTypography tag="span" type="s-body" isLoading={isInitialising} dataTestId="task-id">
        Task ID: {singleJobData?._id || ''}
      </AsyncTypography>
      {singleJobData?.brandbrain_job_id && (
        <>
          |{' '}
          <WppTypography data-testid="ai-id" tag="span" type="s-body">
            AI ID: {singleJobData.brandbrain_job_id}
          </WppTypography>
        </>
      )}
    </>
  )

  const handleSaveEdit = async ({ comment }: OnSubmitGenericModalProps) => {
    try {
      const body: ContentTypePatchRequest = {
        content: contentTypeContent!,
        user_name: userName,
        reason: comment || '',
      }

      await patchContentType(patchContentTypeEndpoint, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })

      const res = await getSingleJobData()
      keywordsAndPhrasesReducerDispatch(setKeywords(res))
      keywordsAndPhrasesReducerDispatch(setKeyphrases(res))

      setContentTypeBeingEdited(null)
      setIsModalOpen(false)
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  const handleRefreshTask = async () => {
    try {
      setIsRefreshLoading(true)
      await getSingleJobData()
    } catch (err) {
      if (setToastDetails)
        setToastDetails({
          type: 'error',
          message: 'Something went wrong while refreshing the task',
        })
    } finally {
      setIsRefreshLoading(false)
    }
  }

  return (
    <div data-testid="pdp-view">
      {isInitialising || isGeneratedContentAvailable ? (
        <>
          <ProductDetail productData={singleJobData} isInitialising={isInitialising} />
          <WppTypography
            tag="p"
            type="s-body"
            className={viewTaskStyles.ids_details}
            data-testid="job-ids-and-brain-job"
          >
            {outputIDs}
          </WppTypography>
          {actions && (
            <RenderNavButtons
              status={singleJobData?.status}
              onDownloadClick={actions.onDownloadClick}
              isDownloading={actions.isDownloading}
              onApproveClick={actions.onApproveClick}
              isCurrentlyEditing={!!contentTypeBeingEdited}
              isEditNeeded
              className={viewTaskStyles.bottomNavigation}
              isInitialising={isInitialising}
              clientId={singleJobData?.client_id}
            />
          )}
        </>
      ) : (
        <div className={viewTaskStyles.messageContainer}>
          <WppTypography className={viewTaskStyles.unavailable} type="s-midi" data-testid="pdp-error-message">
            {returnEmptyPDPMessage(singleJobData?.status)}
          </WppTypography>

          {singleJobData?.status !== 'failed' && (
            <WppButton variant="secondary" onClick={handleRefreshTask} loading={isRefreshLoading}>
              Refresh
            </WppButton>
          )}

          <WppTypography
            tag="p"
            type="s-body"
            className={clsx(viewTaskStyles.ids_details, viewTaskStyles.center)}
            data-testid="job-ids-and-brain-job"
          >
            {outputIDs}
          </WppTypography>
        </div>
      )}

      {/* save edits content */}
      {canPatchJob && isModalOpen && (
        <GenericModal
          modalTitle="Save edited content"
          submitButtonText="Continue"
          isApiLoading={isSingleJobLoading || isPatchContentTypeLoading}
          shouldModalOpen={canPatchJob && isModalOpen}
          fieldsConfig={{
            hasCommentField: true,
            isCommentRequiredField: false,
          }}
          onCancel={() => setIsModalOpen(false)}
          onSubmit={handleSaveEdit}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-message">
            Are you sure you want to save this edit?
            <br />
            This will move content back to <b>pending approval</b> status.
          </WppTypography>
        </GenericModal>
      )}
    </div>
  )
}

export default PdpView
